import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileExcelOutlined,
  RollbackOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Drawer,
  Skeleton,
  Space,
  Tabs,
  Tag,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import CapitalTab from './CapitalTab';
import HeaderTab from './HeaderTab';
import OwnersTab from './OwnersTab';
import TransactionsTab from './TransactionsTab';
import { FINAL_QUARTER, date, errorText, saveBlob } from './format';

// Which tab fixes which problem, so a click on a problem opens the right one.
const TAB_OF_FIELD = (field = '') => {
  if (
    field.startsWith('header') ||
    field === 'entityType' ||
    field === 'investmentType'
  )
    return 'header';
  if (field.startsWith('owners') || field === 'apr') return 'owners';
  if (field.startsWith('table4') || field === 'rate') return 'capital';
  if (['investments', 'payouts', 'table6'].includes(field))
    return 'transactions';
  return null;
};

/**
 * One company's DI1 for the period: header data, owners, capital (Table 4)
 * and the quarter's transactions, with everything NBS would reject listed on
 * top. Each tab saves on its own.
 *
 * @param {Object} props
 * @param {string|null} props.companyId - Open company, or null when closed
 * @param {{year: number, quarter: number}} props.period
 * @param {Function} props.onClose
 * @param {Function} props.onChanged - After anything was saved
 * @returns {JSX.Element}
 */
export default function CompanyDrawer({
  companyId,
  period,
  onClose,
  onChanged,
}) {
  const { t } = useTranslation('diReports');
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('header');
  const [downloading, setDownloading] = useState(false);
  const queryKey = ['di-report', companyId, period.year, period.quarter];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => API.diReports.getCompany(companyId, period),
    enabled: Boolean(companyId),
  });

  const changed = (fresh) => {
    if (fresh?.company) queryClient.setQueryData(queryKey, fresh);
    else queryClient.invalidateQueries({ queryKey });
    onChanged?.();
  };

  const sentMutation = useMutation({
    mutationFn: (sent) => API.diReports.setSent(companyId, period, sent),
    onSuccess: (fresh) => {
      message.success(t('messages.saved'));
      changed(fresh);
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const download = async () => {
    setDownloading(true);
    try {
      const { blob, fileName } = await API.diReports.downloadFile(
        companyId,
        period
      );
      saveBlob(blob, fileName);
    } catch (e) {
      message.error(await errorText(e));
    } finally {
      setDownloading(false);
    }
  };

  const sent = data?.report?.status === 'SENT';
  // Made by hand outside the platform: the checks do not apply.
  const manual = Boolean(data?.profile?.manual);
  const errors = manual
    ? []
    : (data?.issues || []).filter((i) => i.level === 'error');
  const warnings = manual
    ? []
    : (data?.issues || []).filter((i) => i.level === 'warning');
  const periodLabel =
    period.quarter === FINAL_QUARTER
      ? t('quarter.finalOf', { year: period.year })
      : t('quarter.of', { q: period.quarter, year: period.year });

  const issueList = (list, type) =>
    list.length > 0 && (
      <Alert
        type={type}
        showIcon
        className="!static"
        message={
          <ul className="m-0 pl-4">
            {list.map((i, idx) => {
              const target = TAB_OF_FIELD(i.field);
              return (
                <li key={idx}>
                  {target ? (
                    <a onClick={() => setTab(target)}>{i.message}</a>
                  ) : (
                    i.message
                  )}
                </li>
              );
            })}
          </ul>
        }
      />
    );

  return (
    <Drawer
      open={Boolean(companyId)}
      onClose={onClose}
      width={1000}
      destroyOnClose
      title={
        data ? (
          <div>
            <div>{data.company.name}</div>
            <div className="text-xs font-normal opacity-60">
              {t('columns.mb')} {data.company.mb} · {periodLabel} ·{' '}
              {data.fileName}
            </div>
          </div>
        ) : (
          t('drawer.loading')
        )
      }
      extra={
        data && (
          <Space>
            <Button
              shape="round"
              icon={<FileExcelOutlined />}
              loading={downloading}
              onClick={download}
            >
              {t('actions.download')}
            </Button>
            {sent ? (
              <Button
                shape="round"
                icon={<RollbackOutlined />}
                loading={sentMutation.isPending}
                onClick={() => sentMutation.mutate(false)}
              >
                {t('actions.backToWork')}
              </Button>
            ) : (
              <Button
                shape="round"
                type="primary"
                icon={<SendOutlined />}
                disabled={errors.length > 0}
                loading={sentMutation.isPending}
                onClick={() => sentMutation.mutate(true)}
              >
                {t('actions.markSent')}
              </Button>
            )}
          </Space>
        )
      }
    >
      {isLoading && <Skeleton active />}
      {error && (
        <Alert
          type="error"
          showIcon
          className="!static"
          message={error?.response?.data?.message || error.message}
        />
      )}
      {data && (
        <div className="flex flex-col gap-3">
          {sent && (
            <Alert
              type="info"
              showIcon
              className="!static"
              message={t('drawer.sentInfo', { date: date(data.report.sentAt) })}
            />
          )}
          {manual && (
            <Alert
              type="info"
              showIcon
              className="!static"
              message={t('drawer.manualInfo', {
                reason: data.profile.manualReason || t('header.manualDefault'),
              })}
            />
          )}
          {issueList(errors, 'error')}
          {issueList(warnings, 'warning')}
          {!manual && !errors.length && !sent && (
            <Tag color="green">{t('drawer.ready')}</Tag>
          )}

          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              {
                key: 'header',
                label: t('tabs.header'),
                children: (
                  <HeaderTab
                    data={data}
                    companyId={companyId}
                    onSaved={changed}
                  />
                ),
              },
              {
                key: 'owners',
                label: t('tabs.owners'),
                children: (
                  <OwnersTab
                    data={data}
                    companyId={companyId}
                    onSaved={changed}
                  />
                ),
              },
              {
                key: 'capital',
                label: t('tabs.capital'),
                children: (
                  <CapitalTab
                    data={data}
                    companyId={companyId}
                    period={period}
                    locked={sent}
                    onSaved={changed}
                  />
                ),
              },
              {
                key: 'transactions',
                label: t('tabs.transactions'),
                children: (
                  <TransactionsTab
                    data={data}
                    companyId={companyId}
                    period={period}
                    locked={sent}
                    onSaved={changed}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
    </Drawer>
  );
}
